import { useState, useEffect } from 'react';

/**
 * Custom hook for screen size detection
 * Returns isMobile, isTablet, isDesktop, and current width
 * 
 * Breakpoints:
 * - Mobile: < 992px
 * - Tablet: < 1100px
 * - Desktop: >= 1100px
 */
export const useScreenSize = () => {
  // Initialize with correct values immediately to avoid false initial state
  const getInitialSize = () => {
    if (typeof window === 'undefined') {
      return {
        width: 1920,
        isMobile: false,
        isTablet: false,
        isDesktop: true,
      };
    }
    const width = window.innerWidth;
    return {
      width,
      isMobile: width < 992,
      isTablet: width < 1100 && width >= 992,
      isDesktop: width >= 1100,
    };
  };

  const [screenSize, setScreenSize] = useState(getInitialSize);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const isMobile = width < 992;
      const isTablet = width < 1100 && width >= 992;
      const isDesktop = width >= 1100;

      setScreenSize({
        width,
        isMobile,
        isTablet,
        isDesktop,
      });
    };

    // Set initial size (in case it changed between render and effect)
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return screenSize;
};

export default useScreenSize;
